import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setStatus('loading');
    setErrorMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9ue3it3pcjqs2fcspgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Not JSON
      }

      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string)
        || (parsed?.meta as Record<string, unknown>)?.detail as string
        || responseText;

      if (response.ok && (parsed as Record<string, unknown>)?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(String(serverMsg || 'Ocurrió un error al enviar el mensaje. Intente nuevamente.'));
      }
    } catch {
      setStatus('error');
      setErrorMessage('Error de conexión. Por favor verifique su internet e intente nuevamente.');
    }
  };

  return (
    <section className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left - Info */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-6">
                <i className="ri-mail-send-line text-sm w-4 h-4 flex items-center justify-center" />
                Estemos en Contacto
              </div>

              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Información de{' '}
                <span className="text-primary-500">Contacto</span>
              </h2>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8 text-justify">
                Estamos listos para atender sus consultas, cotizaciones y requerimientos técnicos.
                Nuestro equipo de asesores químicos está disponible para brindarle el mejor soporte.
              </p>

              <div className="space-y-4">
                {[
                  {
                    icon: 'ri-phone-line',
                    title: 'Teléfono',
                    detail: '+58 412 101 9460',
                    sub: 'Lun - Vie, 8:00 AM - 5:00 PM',
                    key: 'phone-1',
                  },
                  {
                    icon: 'ri-phone-line',
                    title: 'Teléfono',
                    detail: '+58 412 908 0594',
                    sub: 'Lun - Vie, 8:00 AM - 5:00 PM',
                    key: 'phone-2',
                  },
                  {
                    icon: 'ri-mail-line',
                    title: 'Correo Electrónico',
                    detail: 'info@resinvenca.com',
                    sub: 'Respuesta en menos de 24 horas',
                    key: 'email-info',
                  },
                  {
                    icon: 'ri-mail-line',
                    title: 'Ventas',
                    detail: 'ventas@resinvenca.com',
                    sub: 'Respuesta en menos de 24 horas',
                    key: 'email-ventas',
                  },
                  {
                    icon: 'ri-map-pin-line',
                    title: 'Ubicación',
                    detail: 'Venezuela',
                    sub: 'Cobertura nacional e internacional',
                    key: 'location',
                  },
                  {
                    icon: 'ri-whatsapp-line',
                    title: 'WhatsApp',
                    detail: '+58 412 101 9460',
                    sub: 'Respuesta inmediata',
                    key: 'whatsapp-1',
                    href: 'https://wa.me/584121019460',
                  },
                  {
                    icon: 'ri-whatsapp-line',
                    title: 'WhatsApp',
                    detail: '+58 412 908 0594',
                    sub: 'Respuesta inmediata',
                    key: 'whatsapp-2',
                    href: 'https://wa.me/584129080594',
                  },
                ].map((item: { icon: string; title: string; detail: string; sub: string; key: string; href?: string }) => (
                  <div key={item.key} className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <div>
                      <p className="text-foreground-950 font-semibold text-sm">{item.title}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-foreground-700 text-sm hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                        >
                          {item.detail}
                        </a>
                      ) : (
                        <p className="text-foreground-700 text-sm">{item.detail}</p>
                      )}
                      <p className="text-foreground-400 text-xs mt-0.5">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 mt-8">
                <a
                  href="https://www.instagram.com/resinvenca29chemicals"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 hover:scale-105 transition-all duration-300 cursor-pointer"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a
                  href="https://wa.me/584129080594"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 hover:scale-105 transition-all duration-300 cursor-pointer"
                  aria-label="WhatsApp"
                >
                  <i className="ri-whatsapp-fill text-lg"></i>
                </a>
                <span className="text-foreground-500 text-sm ml-1">Síguenos en redes</span>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
                Solicitar Información
              </h3>

              {status === 'success' ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h4 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                    ¡Mensaje Enviado con Éxito!
                  </h4>
                  <p className="text-foreground-600 text-sm">
                    Gracias por contactarnos. Un especialista de RESINVENCA RESINAS DE VENEZUELA C.A.
                    se comunicará con usted en las próximas 24 horas.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Enviar Otro Mensaje
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="space-y-5"
                  noValidate
                >
                  {/* Honeypot */}
                  <div className="contact-hp-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="Ing. Juan Pérez"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        Correo Electrónico *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="jperez@empresa.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-company" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        Empresa *
                      </label>
                      <input
                        type="text"
                        id="contact-company"
                        name="company"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="Nombre de su empresa"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-foreground-700 text-xs font-medium mb-1.5">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="contact-phone"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="+58 000 000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-foreground-700 text-xs font-medium mb-1.5">
                      Producto o Servicio de Interés
                    </label>
                    <select
                      id="contact-subject"
                      name="interest"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all cursor-pointer"
                    >
                      <option value="">Seleccione una opción...</option>
                      <option value="Demulsificante para Crudo">Demulsificante para Crudo</option>
                      <option value="Cotización General">Cotización General</option>
                      <option value="Programa de Revendedores">Programa de Revendedores</option>
                      <option value="Contrato PDVSA">Contrato PDVSA</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-foreground-700 text-xs font-medium mb-1.5">
                      Mensaje *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                      placeholder="Describa sus requerimientos, volúmenes estimados y cualquier especificación técnica relevante..."
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">Máximo 500 caracteres</p>
                  </div>

                  {status === 'error' && errorMessage && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-sm"></i>
                      </div>
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Enviar Solicitud
                        <i className="ri-send-plane-line"></i>
                      </span>
                    )}
                  </button>

                  <p className="text-foreground-400 text-xs text-center">
                    Sus datos están seguros. No compartimos información con terceros.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}